import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchActiveCountries } from '@/shared/api/places';
import { toOptions } from '@/shared/lib/options';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { toSearchParams } from '@/shared/lib/search';
import { useListFilters } from '@/shared/lib/useListFilters';
import { FilterActions } from '@/shared/ui/FilterActions';
import { FilterField } from '@/shared/ui/FilterField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { SearchKeyInput } from '@/shared/ui/SearchKeyInput';
import { SortableTh } from '@/shared/ui/SortableTh';
import { fetchRegionOptions } from '@/pages/place/api';
import { fetchSeoCities, type SeoCity } from './api';
import { OnlySeoActiveFilter, SitemapToggleCell } from './SitemapToggle';

const LIST_KEY = 'seo-cities';

const SEARCH_KEY_OPTIONS = [
  { value: 'id', label: 'Поиск по id' },
  { value: 'name', label: 'Поиск по городу' },
];

const emptyFilters = { searchKey: '', searchValue: '', countryId: '', regionId: '' };

export const SeoCitiesPage = () => {
  const list = useListFilters(emptyFilters);
  const { draft, applied, page, sortField, sortOrder } = list;
  const [onlyActive, setOnlyActive] = useState(false);

  const countries = useQuery({ queryKey: ['countries-active'], queryFn: fetchActiveCountries });
  const regions = useQuery({
    queryKey: ['region-options', draft.countryId],
    queryFn: () => fetchRegionOptions(draft.countryId),
  });

  const params = {
    page,
    sort_field: sortField,
    sort_order: sortOrder,
    ...toSearchParams(applied.searchKey, applied.searchValue),
    country_id: applied.countryId,
    region_id: applied.regionId,
    is_seo_active: onlyActive ? (1 as const) : undefined,
  };
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [LIST_KEY, params],
    queryFn: () => fetchSeoCities(params),
  });
  const rows = getListRows<SeoCity>(data, ['cities']);

  return (
    <>
      <section className="content-header">
        <h1>Города</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <form onSubmit={list.submit}>
              <FilterField label="Поиск">
                <SearchKeyInput
                  keyOptions={SEARCH_KEY_OPTIONS}
                  searchKey={draft.searchKey}
                  searchValue={draft.searchValue}
                  onKeyChange={(v) => list.setField('searchKey', v)}
                  onValueChange={(v) => list.setField('searchValue', v)}
                />
              </FilterField>
              <FilterField label="Фильтрация по стране">
                <FilterSelect
                  options={toOptions(countries.data)}
                  value={draft.countryId}
                  isLoading={countries.isLoading}
                  onChange={(countryId) => list.patchDraft({ countryId, regionId: '' })}
                />
              </FilterField>
              <FilterField label="Фильтрация по региону">
                <FilterSelect
                  options={toOptions(regions.data)}
                  value={draft.regionId}
                  isLoading={regions.isFetching}
                  onChange={(v) => list.setField('regionId', v)}
                />
              </FilterField>
              <FilterActions disabled={isFetching} onReset={list.reset} />
            </form>
          </div>
          <div className="box-header with-border">
            <OnlySeoActiveFilter
              checked={onlyActive}
              onChange={(checked) => {
                setOnlyActive(checked);
                list.setPage(1);
              }}
            />
          </div>
          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки городов" />
            <table className="table table-bordered">
              <thead>
                <tr>
                  <SortableTh active={sortField === 'id'} onClick={() => list.toggleSort('id')}>
                    id
                  </SortableTh>
                  <SortableTh active={sortField === 'name'} onClick={() => list.toggleSort('name')}>
                    Город
                  </SortableTh>
                  <SortableTh
                    active={sortField === 'country_id'}
                    onClick={() => list.toggleSort('country_id')}
                  >
                    Страна
                  </SortableTh>
                  <SortableTh
                    active={sortField === 'region_id'}
                    onClick={() => list.toggleSort('region_id')}
                  >
                    Регион
                  </SortableTh>
                  <th>Участвует в структуре сайта</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((city) => (
                  <tr key={city.id}>
                    <td>{city.id}</td>
                    <td>{city.name}</td>
                    <td>{city.country || 'No country name'}</td>
                    <td>{city.region || 'No region name'}</td>
                    <td>
                      <SitemapToggleCell
                        entity="city"
                        id={city.id}
                        isActive={Boolean(city.is_seo_active)}
                        listKey={LIST_KEY}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={getTotalPages(data)} onChange={list.setPage} />
          </div>
        </div>
      </section>
    </>
  );
};
